/**
 * Picks specified keys from an object and returns a new object with only those keys.
 *
 * @see https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_pick
 */
export const pick = <T extends object, K extends keyof T>(object: T, keys: K[]): Pick<T, K> => {
  return keys.reduce(
    (obj, key) => {
      if (object && object.hasOwnProperty(key)) {
        obj[key] = object[key];
      }
      return obj;
    },
    {} as Pick<T, K>,
  );
};

/** Omits specified keys from an object and returns a new object without those keys. */
export const omit = <T extends object, K extends keyof T>(object: T, keys: K[]): Omit<T, K> => {
  const clonedObject = { ...object };
  return keys.reduce((obj, key) => {
    if (object && object.hasOwnProperty(key)) {
      delete obj[key];
    }
    return obj;
  }, clonedObject as T);
};

/**
 * Gets the value at path of object
 *
 * @source https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_get
 */
export const get = <T>(object: unknown, path: string, defaultValue?: T): T | undefined => {
  const travel = (regexp: RegExp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), object as any);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === object ? defaultValue : (result as T);
};
